<template>
  <div
    v-if="items.length"
    ref="parentGrid"
    class="w-full overflow-x-auto overflow-y-hidden overscroll-x-contain [scrollbar-gutter:auto] supports-[overflow-x:overlay]:[overflow-x:overlay] lg:overflow-x-hidden lg:overscroll-y-contain lg:overscroll-x-auto"
    :class="[
      ...parentGridClass,
      {
        'grid-flow-col-dense [grid-auto-columns:max-content]': items.length > 1,
      },
    ]"
  >
    <article class="relative md:pl-0" :class="primaryImageWrapperClasses">
      <slot :items name="first" />
    </article>
    <div class="relative overflow-hidden">
      <ul
        v-if="items.length > 1"
        ref="scrollableArea"
        class="h-full w-full list-none gap-12 overflow-x-auto overflow-y-hidden overscroll-x-contain whitespace-nowrap pr-24 [scrollbar-gutter:auto] md:pr-0 lg:w-fit lg:overscroll-y-contain lg:overscroll-x-auto"
        :class="[
          staticGridClass,
          mobileGridClass,
          tabletGridClass,
          desktopGridClass,
          secondaryListClasses,
        ]"
      >
        <template
          v-for="(item, index) in items"
          :key="'carousel-item-' + (item.src ?? index)"
        >
          <slot :index :item name="rest" />
        </template>
      </ul>
      <!-- Gradients, start and end -->
      <Transition
        :css="false"
        @enter="onGradientEnter"
        @leave="onGradientLeave"
      >
        <div
          v-if="shouldShowStartGradient"
          ref="startGradient"
          :class="startGradientClasses"
        />
      </Transition>

      <Transition
        :css="false"
        @enter="onGradientEnter"
        @leave="onGradientLeave"
      >
        <div
          v-if="shouldShowEndGradient"
          ref="endGradient"
          :class="endGradientClasses"
        />
      </Transition>
    </div>
  </div>
</template>

<script
  setup
  lang="ts"
  generic="
    CarouselItem extends { src: string; alt: string; [key: string]: unknown }
  "
>
import {
  type VNode,
  computed,
  onMounted,
  onUnmounted,
  toRefs,
  useTemplateRef,
  watchEffect,
} from 'vue'

import {
  Breakpoint,
  getCurrentBreakpoint,
} from '@backmarket/utils/dom/getCurrentBreakpoint'
import { tw } from '@ds/utils/tailwind'
import { useScroll } from '@vueuse/core'

import { useEmitOnce } from '../../composables/useEmitOnce'

withDefaults(
  defineProps<{
    /**
     * The total length of the items in the Carousel.
     */
    items: CarouselItem[] | []

    primaryImageWrapperClasses?: string
    secondaryListClasses?: string
  }>(),
  {
    items: () => [],
    primaryImageWrapperClasses: '',
    secondaryListClasses: '',
  },
)

defineSlots<{
  first: (props: { items: CarouselItem[] }) => VNode[]

  rest: (props: { index: number; item: CarouselItem }) => VNode[]
}>()

const emit = defineEmits<{
  (e: 'scroll-start'): void
  (e: 'scroll-end'): void
}>()

const parentGrid = useTemplateRef('parentGrid')
const scrollableArea = useTemplateRef('scrollableArea')

// =============================================
// Scroll events
// =============================================

// Parent grid (scrollable area in mobile)
const { arrivedState: arrivedStateParentGrid, measure: measureParentGrid } =
  useScroll(parentGrid)

// Scrollable area (desktop/tablet)
const {
  arrivedState: arrivedStateScrollableArea,
  measure: measureScrollableArea,
} = useScroll(scrollableArea)

// Flags to track if `scroll-start` event have been emitted
let hasScrollStartedParentGrid = false
let hasScrollStartedScrollableArea = false

// I had to duplicate the useEmitOnce because I couldn't
// find a better way to make TS happy
const { emitOnce: emitOnceScrollStart } = useEmitOnce<['scroll-start']>(emit)
const { emitOnce: emitOnceScrollEnd } = useEmitOnce<['scroll-end']>(emit)

watchEffect(() => {
  if (!import.meta.server) {
    const breakpoint = getCurrentBreakpoint()

    // Handle parentGrid (mobile)
    if (breakpoint < Breakpoint.MD) {
      if (!hasScrollStartedParentGrid && !arrivedStateParentGrid.left) {
        hasScrollStartedParentGrid = true
        emitOnceScrollStart('scroll-start')
      }
      if (arrivedStateParentGrid.right) {
        emitOnceScrollEnd('scroll-end')
      }

      return
    }

    // Handle scrollableArea (tablet)
    if (breakpoint > Breakpoint.MD && breakpoint < Breakpoint.LG) {
      if (!hasScrollStartedScrollableArea && !arrivedStateScrollableArea.left) {
        hasScrollStartedScrollableArea = true
        emitOnceScrollStart('scroll-start')
      }
      if (arrivedStateScrollableArea.right) {
        emitOnceScrollEnd('scroll-end')
      }

      return
    }

    // Handle scrollableArea (desktop)
    if (breakpoint >= Breakpoint.LG) {
      if (!hasScrollStartedScrollableArea && !arrivedStateScrollableArea.top) {
        hasScrollStartedScrollableArea = true
        emitOnceScrollStart('scroll-start')
      }
      if (arrivedStateScrollableArea.bottom) {
        emitOnceScrollEnd('scroll-end')
      }
    }
  }
})

// =============================================
// Grid/Flex classes
// =============================================

const parentGridClass = [
  tw`grid gap-12`,
  tw`md:[grid-auto-flow:initial] md:flex md:flex-col md:gap-8`,
  tw`lg:flex-row-reverse`,
]
const staticGridClass = tw`grid gap-12`

/**
 * `grid-flow-col-dense` — the flow is of columns (stacking 2 items on mobile)
 *                         and dense, makes it align the items to the top of the
 *                         column, just in case.
 */
const mobileGridClass = tw`grid-flow-col`

const tabletGridClass = tw`md:gap-8 md:flex md:flex-row md:flex-nowrap`

const desktopGridClass = tw`lg:flex-col lg:overflow-y-auto lg:overflow-x-hidden`

// =============================================
// Scrollable area - Gradients
// =============================================

const isScrollableHorizonally = computed(() => {
  return (
    (scrollableArea.value?.scrollWidth || 0) >
    (scrollableArea.value?.clientWidth || -1)
  )
})

const isScrollableVertically = computed(() => {
  return (
    (scrollableArea.value?.scrollHeight || 0) >
    (scrollableArea.value?.clientHeight || -1)
  )
})

const { arrivedState } = useScroll(scrollableArea)
const { left, right, top, bottom } = toRefs(arrivedState)

const scrollIsAtStartHorizontally = computed(
  () => isScrollableHorizonally.value && left.value,
)
const scrollIsAtEndHorizontally = computed(
  () => isScrollableHorizonally.value && right.value,
)
const scrollIsAtStartVertically = computed(
  () => isScrollableVertically.value && top.value,
)
const scrollIsAtEndVertically = computed(
  () => isScrollableVertically.value && bottom.value,
)

const isOnEdges = computed(
  () =>
    scrollIsAtStartHorizontally.value ||
    scrollIsAtEndHorizontally.value ||
    scrollIsAtStartVertically.value ||
    scrollIsAtEndVertically.value,
)

const gradientSharedClasses = tw`pointer-events-none absolute hidden md:block md:h-[104px] md:w-[52px] lg:w-[104px] lg:h-[52px] z-[2]`

const gradientStartClassHorizontally = tw`md:bg-[linear-gradient(90deg,#fff_0%,rgba(255,255,255,1)_0.03%,rgba(255,255,255,1)_0.24%,rgba(255,255,255,0.99)_0.8%,rgba(255,255,255,0.98)_1.9%,rgba(255,255,255,0.96)_3.7%,rgba(255,255,255,0.94)_6.4%,rgba(255,255,255,0.90)_10.16%,rgba(255,255,255,0.85)_15.17%,rgba(255,255,255,0.78)_21.6%,rgba(255,255,255,0.70)_29.63%,rgba(255,255,255,0.61)_39.44%,rgba(255,255,255,0.49)_51.2%,rgba(255,255,255,0.35)_65.1%,rgba(255,255,255,0.19)_81.3%,rgba(255,255,255,0)_100%)]`
const gradientEndClassHorizontally = tw`md:bg-[linear-gradient(270deg,#fff_0%,rgba(255,255,255,1)_0.03%,rgba(255,255,255,1)_0.24%,rgba(255,255,255,0.99)_0.8%,rgba(255,255,255,0.98)_1.9%,rgba(255,255,255,0.96)_3.7%,rgba(255,255,255,0.94)_6.4%,rgba(255,255,255,0.90)_10.16%,rgba(255,255,255,0.85)_15.17%,rgba(255,255,255,0.78)_21.6%,rgba(255,255,255,0.70)_29.63%,rgba(255,255,255,0.61)_39.44%,rgba(255,255,255,0.49)_51.2%,rgba(255,255,255,0.35)_65.1%,rgba(255,255,255,0.19)_81.3%,rgba(255,255,255,0)_100%)]`
const gradientStartClassVertically = tw` lg:bg-[linear-gradient(180deg,#fff_0%,rgba(255,255,255,1)_0.03%,rgba(255,255,255,1)_0.24%,rgba(255,255,255,0.99)_0.8%,rgba(255,255,255,0.98)_1.9%,rgba(255,255,255,0.96)_3.7%,rgba(255,255,255,0.94)_6.4%,rgba(255,255,255,0.90)_10.16%,rgba(255,255,255,0.85)_15.17%,rgba(255,255,255,0.78)_21.6%,rgba(255,255,255,0.70)_29.63%,rgba(255,255,255,0.61)_39.44%,rgba(255,255,255,0.49)_51.2%,rgba(255,255,255,0.35)_65.1%,rgba(255,255,255,0.19)_81.3%,rgba(255,255,255,0)_100%)]`
const gradientEndClassVertically = tw` lg:bg-[linear-gradient(0deg,#fff_0%,rgba(255,255,255,1)_0.03%,rgba(255,255,255,1)_0.24%,rgba(255,255,255,0.99)_0.8%,rgba(255,255,255,0.98)_1.9%,rgba(255,255,255,0.96)_3.7%,rgba(255,255,255,0.94)_6.4%,rgba(255,255,255,0.90)_10.16%,rgba(255,255,255,0.85)_15.17%,rgba(255,255,255,0.78)_21.6%,rgba(255,255,255,0.70)_29.63%,rgba(255,255,255,0.61)_39.44%,rgba(255,255,255,0.49)_51.2%,rgba(255,255,255,0.35)_65.1%,rgba(255,255,255,0.19)_81.3%,rgba(255,255,255,0)_100%)]`

const positionStartClassHorizontally = tw`top-0 md:left-0`
const positionEndClassHorizontally = tw`top-0 md:right-0`
const positionStartClassVertically = tw`lg:top-0`
const positionEndClassVertically = tw`lg:top-[initial] lg:bottom-0`

// =============================================
// Gradient visibility and classes
// =============================================

const shouldShowStartGradient = computed(() => {
  return (
    (isScrollableHorizonally.value || isScrollableVertically.value) &&
    (!isOnEdges.value ||
      (isScrollableHorizonally.value && !scrollIsAtStartHorizontally.value) ||
      (isScrollableVertically.value && !scrollIsAtStartVertically.value))
  )
})

const shouldShowEndGradient = computed(() => {
  return (
    (isScrollableHorizonally.value || isScrollableVertically.value) &&
    (!isOnEdges.value ||
      (isScrollableHorizonally.value && !scrollIsAtEndHorizontally.value) ||
      (isScrollableVertically.value && !scrollIsAtEndVertically.value))
  )
})

const startGradientClasses = computed(() => [
  gradientSharedClasses,
  {
    [positionStartClassHorizontally]: !scrollIsAtStartHorizontally.value,
    [gradientStartClassHorizontally]: !scrollIsAtStartHorizontally.value,
    [positionStartClassVertically]: !scrollIsAtStartVertically.value,
    [gradientStartClassVertically]: !scrollIsAtStartVertically.value,
  },
])

const endGradientClasses = computed(() => [
  gradientSharedClasses,
  {
    [positionEndClassHorizontally]: !scrollIsAtEndHorizontally.value,
    [gradientEndClassHorizontally]: !scrollIsAtEndHorizontally.value,
    [positionEndClassVertically]: !scrollIsAtEndVertically.value,
    [gradientEndClassVertically]: !scrollIsAtEndVertically.value,
  },
])

// =============================================
// Transition hooks
// =============================================
let timeout: ReturnType<typeof setTimeout> | null = null

const onGradientEnter = (el: Element, done: () => void) => {
  if (timeout) clearTimeout(timeout)
  const element = el as HTMLElement

  requestAnimationFrame(() => {
    element.style.opacity = '0'
    // forces reflow (style recalculation and paint)
    void element.offsetHeight
  })

  // Second requestAnimationFrame to ensure the style is set
  requestAnimationFrame(() => {
    element.style.transition = 'opacity 300ms ease-in-out'
    element.style.opacity = '1'
    timeout = setTimeout(done, 300)
  })
}

const onGradientLeave = (el: Element, done: () => void) => {
  if (timeout) clearTimeout(timeout)
  const element = el as HTMLElement
  element.style.transition = 'opacity 300ms ease-in-out'
  element.style.opacity = '0'

  timeout = setTimeout(done, 300)
}

onMounted(() => {
  try {
    measureParentGrid()
    measureScrollableArea()
  } catch {
    // do nothing here
  }
})

onUnmounted(() => {
  if (timeout) clearTimeout(timeout)
})
</script>
